/**
 * Input mask engine, shared between the builder, the inductee renderer, and
 * the server validator so all three agree on what a mask accepts.
 *
 * Grammar (matches the familiar form-builder convention):
 *   @  one letter        [A-Za-z]
 *   #  one digit         [0-9]
 *   *  letter or digit   [A-Za-z0-9]
 * Any other character is a literal: it is shown in place and auto-inserted as
 * the user types (e.g. the spaces in "@@ ###### @").
 */

const TOKEN_PATTERN: Record<string, RegExp> = {
  '@': /[A-Za-z]/,
  '#': /[0-9]/,
  '*': /[A-Za-z0-9]/,
};

/** True if `ch` is a mask token (a fillable position), not a literal. */
export function isMaskToken(ch: string): boolean {
  return ch === '@' || ch === '#' || ch === '*';
}

/** True if the mask has at least one fillable position. */
export function maskHasToken(mask: string): boolean {
  return [...mask].some(isMaskToken);
}

function escapeRegExp(ch: string): string {
  return ch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Format raw input against the mask: keep only characters that fit each token
 * (skipping ones that don't), auto-insert literals, and stop at the first
 * unfilled token once input runs out. Used for as-you-type masking, so it
 * returns the formatted partial value.
 */
export function applyMask(mask: string, raw: string): string {
  const input = [...raw];
  let out = '';
  // Literals are held until the next token fills, so a trailing separator is
  // never emitted on its own. Without this, deleting a trailing separator
  // would be undone on the next keystroke (the field would feel frozen).
  let pending = '';
  let ri = 0;
  for (const mc of mask) {
    if (isMaskToken(mc)) {
      let matched = '';
      while (ri < input.length) {
        const c = input[ri++]!;
        if (TOKEN_PATTERN[mc]!.test(c)) {
          matched = c;
          break;
        }
        // Char doesn't fit this token (e.g. a stray separator) - drop it.
      }
      if (!matched) break; // no more usable input: leave the rest unfilled
      out += pending + matched;
      pending = '';
    } else {
      // Consume a matching typed literal so it doesn't double up, and defer
      // emitting it until a later token actually fills.
      if (ri < input.length && input[ri] === mc) ri++;
      pending += mc;
    }
  }
  return out;
}

/** Anchored regex that matches a fully-completed value for the mask. */
export function maskToRegExp(mask: string): RegExp {
  let src = '^';
  for (const mc of mask) {
    if (mc === '@') src += '[A-Za-z]';
    else if (mc === '#') src += '[0-9]';
    else if (mc === '*') src += '[A-Za-z0-9]';
    else src += escapeRegExp(mc);
  }
  return new RegExp(src + '$');
}

/** True if `value` completely satisfies the mask (every token filled). */
export function isMaskComplete(mask: string, value: string): boolean {
  return maskToRegExp(mask).test(value);
}

/** Human-facing placeholder: literals shown, each fillable position as "_". */
export function maskPlaceholder(mask: string): string {
  return [...mask].map((c) => (isMaskToken(c) ? '_' : c)).join('');
}
